
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCharacterOperations } from "./useCharacterOperations";
import { useToast } from "@/hooks/use-toast";

const ITEMS_PER_PAGE = 12;

export const useCharactersManager = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const { deleteCharacter } = useCharacterOperations();
  const { toast } = useToast();

  const {
    data: charactersData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["characters", searchTerm, statusFilter, currentPage],
    queryFn: async () => {
      let query = supabase
        .from("characters")
        .select("*", { count: "exact" })
        .order("display_order", { ascending: true })
        .order("created_at", { ascending: false });

      // Apply search filter
      if (searchTerm.trim()) {
        query = query.or(
          `name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,species.ilike.%${searchTerm}%,role.ilike.%${searchTerm}%`
        );
      }

      // Apply status filter
      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter as "draft" | "published" | "archived");
      }

      // Apply pagination
      const from = (currentPage - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        console.error("Error fetching characters:", error);
        throw error;
      }

      return {
        characters: data || [],
        totalCount: count || 0,
      };
    },
  });

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const handleDelete = async (id: string) => {
    try {
      await deleteCharacter(id);
      refetch();
    } catch (error) {
      console.error("Failed to delete character:", error);
    }
  };

  const characters = charactersData?.characters || [];
  const totalCount = charactersData?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return {
    characters,
    isLoading,
    error,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    currentPage,
    setCurrentPage,
    totalPages,
    totalCount,
    handleDelete,
    refetch,
  };
};
